import type { PublicKey } from '@shared/types';
import type { ActiveCall, CallStatus } from '../store/callStore';

type ActiveCallBaseParams = {
  callSessionId: string;
  remoteIdentityId: string;
  localIdentityId?: string;
  serverId?: string;
  serverName?: string;
  serverUrl?: string;
  isKnownContact?: boolean;
  isTemporaryLinkCall?: boolean;
  remoteDisplayName?: string;
  remoteIdentityName?: string;
  remoteIdentityPublicKey?: PublicKey;
  videoEnabled: boolean;
  audioEnabled: boolean;
  securityVerified?: boolean;
};

export function createOutgoingActiveCall(params: ActiveCallBaseParams & {
  status?: Extract<CallStatus, 'initiating' | 'ringing' | 'connecting'>;
}): ActiveCall {
  return {
    callSessionId: params.callSessionId,
    remoteIdentityId: params.remoteIdentityId,
    localIdentityId: params.localIdentityId,
    serverId: params.serverId,
    serverName: params.serverName,
    serverUrl: params.serverUrl,
    isKnownContact: params.isKnownContact,
    isTemporaryLinkCall: params.isTemporaryLinkCall,
    remoteDisplayName: params.remoteDisplayName,
    remoteIdentityName: params.remoteIdentityName,
    remoteIdentityPublicKey: params.remoteIdentityPublicKey,
    status: params.status || 'initiating',
    connectionPhase: 'preparing',
    isInitiator: true,
    videoEnabled: params.videoEnabled,
    audioEnabled: params.audioEnabled,
    securityVerified: params.securityVerified
  };
}

export function createIncomingActiveCall(params: ActiveCallBaseParams & {
  status?: Extract<CallStatus, 'connecting' | 'active' | 'handoff'>;
  startedAt?: Date;
}): ActiveCall {
  return {
    callSessionId: params.callSessionId,
    remoteIdentityId: params.remoteIdentityId,
    localIdentityId: params.localIdentityId,
    serverId: params.serverId,
    serverName: params.serverName,
    serverUrl: params.serverUrl,
    isKnownContact: params.isKnownContact,
    isTemporaryLinkCall: params.isTemporaryLinkCall,
    remoteDisplayName: params.remoteDisplayName,
    remoteIdentityName: params.remoteIdentityName,
    remoteIdentityPublicKey: params.remoteIdentityPublicKey,
    status: params.status || 'connecting',
    connectionPhase: 'answer_received',
    isInitiator: false,
    startedAt: params.startedAt,
    videoEnabled: params.videoEnabled,
    audioEnabled: params.audioEnabled,
    securityVerified: params.securityVerified
  };
}

export function applyNativeStateTransition(activeCall: ActiveCall, event: {
  state: string;
  muted?: boolean;
  runtimeMode?: string;
  localVideoEnabled?: boolean;
  remoteVideoAvailable?: boolean;
  hadConnected?: boolean;
  connectedAtMs?: number;
}): ActiveCall {
  const normalizedState = String(event.state || '').trim().toLowerCase();
  const mappedStatus: ActiveCall['status'] =
    normalizedState === 'connected' || normalizedState === 'active' ? 'active'
      : normalizedState === 'ringing' ? 'ringing'
        : normalizedState === 'connecting' ? 'connecting'
          : normalizedState === 'handoff' ? 'handoff'
            : activeCall.status;
  const connectedAtMs = Number(event.connectedAtMs || 0);
  const connectedAtDate = connectedAtMs > 0 ? new Date(connectedAtMs) : null;
  const nextStartedAt =
    normalizedState === 'connected' || normalizedState === 'active'
      ? (connectedAtDate || activeCall.startedAt || new Date())
      : activeCall.startedAt;

  return {
    ...activeCall,
    status: mappedStatus,
    startedAt: nextStartedAt,
    nativeState: event.state,
    nativeRuntimeMode: event.runtimeMode ?? activeCall.nativeRuntimeMode,
    localVideoEnabled: event.localVideoEnabled ?? activeCall.localVideoEnabled,
    remoteVideoAvailable: event.remoteVideoAvailable ?? activeCall.remoteVideoAvailable,
    audioEnabled: event.muted === undefined ? activeCall.audioEnabled : !event.muted
  };
}

export function applyNativeUpgradeTransition(activeCall: ActiveCall): ActiveCall {
  return {
    ...activeCall,
    status: 'handoff',
    nativeState: 'upgrade-to-video'
  };
}

export function applySecurityVerifiedTransition(activeCall: ActiveCall, verified: boolean): ActiveCall {
  return {
    ...activeCall,
    securityVerified: verified
  };
}
